"use client";

import { useEffect, useRef, type KeyboardEvent, type SyntheticEvent } from "react";

import { TIME_UNKNOWN_TOGGLE_CLASS } from "@/components/BirthDetailsForm.styles";
import { BIRTH_FORM_LABELS } from "@/components/BirthDetailsForm.i18n";
import TrustStrip from "@/components/TrustStrip";

/**
 * Height-reserving placeholder for a deferred BirthDetailsForm-based tool
 * island. Rendered in two situations:
 *
 * 1. Pre-activation — as the interaction target that triggers loading the
 *    real chunk (`onActivate`), so the tool stays in the first viewport
 *    (rules 43/61) without shipping the heavy form stack in the route's
 *    initial JS.
 * 2. While the dynamic chunk loads (`next/dynamic` `loading:`) — same
 *    markup minus the activation handlers, so the swap never collapses to
 *    0px (rule 58 — never `loading: () => null` for a form/result surface).
 *
 * The shell mirrors BirthDetailsForm's TEXT, field for field, from the same
 * label source and the same props (heading, name, required time, trust
 * strip): the island swaps in 3.6 s after mount without any interaction, and
 * a placeholder with different words made the hydrated page read differently
 * from the server HTML, which the certification's word projection rejects.
 * `BirthToolFormShell.parity.test.tsx` pins the two word sets equal for every
 * calculator's prop set, so a label change on either side fails a test
 * instead of a certification.
 */
export interface BirthToolFormShellProps {
  language?: "en" | "hi";
  submitLabel?: string;
  onActivate?: () => void;
  /** Mirrors BirthDetailsForm `heading` and its position in the page outline. */
  heading?: string;
  headingLevel?: "h2" | "h3";
  /** Mirrors BirthDetailsForm `collectName` (default true). */
  collectName?: boolean;
  /** Mirrors BirthDetailsForm `optionalName`. */
  optionalName?: boolean;
  /** Mirrors BirthDetailsForm `requireBirthTime`. */
  requireBirthTime?: boolean;
  /** Mirrors BirthDetailsForm `timeRequiredHint` (the shared hint names the Lagna). */
  timeRequiredHint?: string;
  /** Mirrors BirthDetailsForm `showTrustStrip` (checkout badge off, like the form). */
  showTrustStrip?: boolean;
  /** Wrapper layout; match the island's own form width so the swap does not reflow. */
  className?: string;
  /** Mirrors BirthDetailsForm `fieldIdScope`: the focus hand-off finds `<field>-<scope>`. */
  fieldIdScope?: string;
}

/** Shell input name -> the real BirthDetailsForm field it stands in for. */
const REAL_FIELD_BY_SHELL_NAME: Readonly<Record<string, string>> = {
  name: "name",
  dob: "birthDate",
  tob: "birthTime",
  place: "birthPlace",
};

const HANDOFF_TTL_MS = 10_000;
const HANDOFF_FRAMES = 30;

/**
 * The field the seeker tapped on a shell, carried across the shell -> chunk
 * reserve -> real form swaps (each a different component instance, so this
 * lives at module scope). One deferred calculator per page.
 */
let pendingShellField: { realField: string; at: number } | null = null;

function rememberShellField(event: SyntheticEvent): void {
  const target = event.target;
  const name = target instanceof HTMLInputElement ? target.name : "";
  const realField = REAL_FIELD_BY_SHELL_NAME[name];
  if (realField) pendingShellField = { realField, at: Date.now() };
}

/** BirthDetailsForm's `fieldId(name, heading)` convention (pinned by a test). */
function realFieldId(realField: string, heading?: string): string {
  return `${realField}-${heading || "default"}`;
}

/**
 * After a shell unmounts, focus the real field the seeker tapped once it
 * exists and is enabled — unless focus has already moved somewhere else.
 * Before this, the swap dropped focus to <body> and a phone user had to tap
 * the field a second time (C08: /hi/dasamsa, /ascendant-calculator).
 */
function handOffFocus(heading: string | undefined, framesLeft = HANDOFF_FRAMES): void {
  const pending = pendingShellField;
  if (typeof document === "undefined") return;
  if (!pending || Date.now() - pending.at > HANDOFF_TTL_MS) {
    pendingShellField = null;
    return;
  }
  const target = document.getElementById(realFieldId(pending.realField, heading));
  const active = document.activeElement;
  if (active && active !== document.body) {
    if (target) pendingShellField = null;
    return;
  }
  if (target instanceof HTMLInputElement && !target.disabled) {
    pendingShellField = null;
    target.focus();
    return;
  }
  // Still the chunk-loading shell, or the real input is not hydrated yet.
  if (framesLeft > 0 && typeof requestAnimationFrame === "function") {
    requestAnimationFrame(() => handOffFocus(heading, framesLeft - 1));
  }
}

/** A read-only shell field must never let Space scroll the page. */
function blockSpaceScroll(event: KeyboardEvent<HTMLDivElement>): void {
  if (event.key === " " && event.target instanceof HTMLInputElement) event.preventDefault();
}

export default function BirthToolFormShell({
  language = "en",
  submitLabel,
  onActivate,
  heading,
  headingLevel: Heading = "h3",
  collectName = true,
  optionalName = false,
  requireBirthTime = false,
  timeRequiredHint,
  showTrustStrip = false,
  className = "mx-auto max-w-md",
  fieldIdScope,
}: BirthToolFormShellProps) {
  const labels = BIRTH_FORM_LABELS[language];
  const idScope = fieldIdScope ?? heading;
  const headingRef = useRef(idScope);
  useEffect(() => {
    headingRef.current = idScope;
  }, [idScope]);
  // Unmount = the real form (or its chunk reserve) took this shell's place.
  useEffect(
    () => () => {
      requestAnimationFrame(() => handOffFocus(headingRef.current));
    },
    []
  );
  const activate = (event: SyntheticEvent) => {
    rememberShellField(event);
    onActivate?.();
  };
  return (
    <div
      translate="no"
      className={className}
      onPointerDown={activate}
      onFocus={activate}
      onKeyDown={blockSpaceScroll}
    >
      <form
        translate="no"
        aria-busy="true"
        className="card-sacred min-h-[420px] p-6 sm:p-8 space-y-5"
        onSubmit={(event) => {
          event.preventDefault();
          onActivate?.();
        }}
      >
        {showTrustStrip && <TrustStrip language={language} showCheckout={false} />}
        {heading && (
          <Heading className="font-serif text-body font-bold text-text-primary">{heading}</Heading>
        )}
        {collectName && (
          <ShellField
            label={optionalName ? `${labels.yourName} ${labels.optional}` : labels.yourName}
            name="name"
            placeholder={optionalName ? labels.namePlaceholderOptional : labels.enterFullName}
          />
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ShellField label={labels.birthDate} name="dob" type="date" />
          <ShellBirthTimeField
            labels={labels}
            requireBirthTime={requireBirthTime}
            timeRequiredHint={timeRequiredHint}
          />
        </div>
        <ShellField label={labels.birthPlace} name="place" placeholder={labels.placePlaceholder} />
        <button type="submit" className={SHELL_SUBMIT_CLASS}>
          {submitLabel ?? labels.defaultSubmit} &rarr;
        </button>
      </form>
    </div>
  );
}

// The live form's boxes, so the swap from shell to form moves nothing: the
// inputs are @ishvaram/ui Input (Input.web.tsx) and the submit is its primary
// Button with BirthDetailsForm's "w-full py-3.5". Until 2026-10-11 the shell drew
// 44 px inputs (the live ones are 48) and a 50 px submit without the button's
// side padding, so when an island arrived more than 500 ms after the tap the
// fields below the first moved 4-7 px and a long Hindi submit label wrapped
// differently at 320 px (measured on /hi/kaal-sarp-dosha/, /hi/pitra-dosha/ and
// /rashi/mesh/; after this change every input, hint and submit sits in the same
// place in both at 320-1280 px). The shell heights the lazy boundaries' comments
// cite (420, 427, 435) predate it: the shell is now as tall as the live form,
// which every reserved slot already covers, as each reserves the tallest live
// state (the form with an engine error).
const SHELL_INPUT_CLASS =
  "input-sacred block min-h-11 w-full rounded-lg px-4 py-3 text-body text-text-primary";
const SHELL_SUBMIT_CLASS =
  "inline-flex min-h-11 w-full items-center justify-center rounded-lg px-6 py-3.5 " +
  "text-button font-bold bg-surface-brand-strong text-text-on-brand";

function ShellField({
  label,
  name,
  type = "text",
  placeholder,
}: {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-body-small font-semibold text-text-primary">{label}</span>
      <input
        type={type}
        name={name}
        placeholder={placeholder ?? label}
        readOnly
        aria-label={label}
        className={SHELL_INPUT_CLASS}
      />
    </label>
  );
}

function ShellBirthTimeField({
  labels,
  requireBirthTime,
  timeRequiredHint,
}: {
  labels: (typeof BIRTH_FORM_LABELS)[keyof typeof BIRTH_FORM_LABELS];
  requireBirthTime: boolean;
  timeRequiredHint?: string;
}) {
  return (
    <div>
      <label className="block">
        <span className="mb-2 block text-body-small font-semibold text-text-primary">
          {labels.birthTime}{" "}
          <span className="font-normal text-text-muted">
            {requireBirthTime ? labels.required : labels.optional}
          </span>
        </span>
        <input
          type="time"
          name="tob"
          readOnly
          aria-label={labels.birthTime}
          className={SHELL_INPUT_CLASS}
        />
      </label>
      <p className="mt-1.5 text-caption text-text-muted">
        {requireBirthTime ? (timeRequiredHint ?? labels.timeRequiredHint) : labels.timeOptionalHint}
      </p>
      {!requireBirthTime && (
        <div className="mt-1.5">
          <div className="flex">
            <button type="button" className={TIME_UNKNOWN_TOGGLE_CLASS}>
              {labels.timeUnknownToggle}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
